import { useState, useEffect } from 'react';
import useSWR from 'swr';
import {
  TextInput,
  Select,
  SelectItem,
  Checkbox,
} from 'carbon-components-react';
import { useClassList } from '../../../../api/class.service';
import StageIndicator from '../stage-indicator/StageIndicator';
import { useReducerSpread } from '../../../../utils/common.util';
import CustomButton from '../../../../components/custom-button/custom-btn.component';
import { removeBillItem } from '../financial-aid.service';
import './../financial-aid.style.scss';

export default function LinkBillItems({
  program,
  onSave,
  onBack,
  initialData,
  isLoading: isLoadingProp = false,
  hasError = false,
  isEditMode = false,
  programId = null,
}) {
  const { allClasses } = useClassList();

  const { data: billItems, isLoading: isFetchingBillItems } =
    useSWR(`billing/fees/`);

  const defaultFormData = {
    selectedBillItems: [],
    overrideAmounts: {},
    classId: allClasses && allClasses.length > 0 ? allClasses[0].id : '',
    selectedAvailableFees: [],
  };

  const initialFormData = initialData
    ? {
        ...defaultFormData,
        ...initialData,
        selectedBillItems: initialData.selectedBillItems || [],
        overrideAmounts: initialData.overrideAmounts || {},
      }
    : defaultFormData;

  const [formData, setFormData] = useReducerSpread(initialFormData);

  const [initiallyLinkedIds, setInitiallyLinkedIds] = useState([]);
  const [newlyAddedIds, setNewlyAddedIds] = useState(new Set());
  const [removalError, setRemovalError] = useState(null);
  const [removingItemId, setRemovingItemId] = useState(null);
  const [classConflictError, setClassConflictError] = useState(null);

  useEffect(() => {
    if (
      initialData &&
      initialData.selectedBillItems &&
      initialData.selectedBillItems.length > 0
    ) {
      setInitiallyLinkedIds(initialData.selectedBillItems);
    }
  }, [initialData]);

  useEffect(() => {
    if (allClasses && allClasses.length > 0 && !formData.classId) {
      setFormData({ classId: allClasses[0].id.toString() });
    }
  }, [allClasses]);

  const filteredAvailableFees =
    billItems?.filter((item) => {
      if (!formData.classId || formData.classId === '') {
        return item.classId === null;
      }
      const selectedClassId = parseInt(formData.classId, 10);
      return item.classId === null || item.classId === selectedClassId;
    }) || [];

  const selectedFeesDetails =
    billItems?.filter((item) => formData.selectedBillItems.includes(item.id)) ||
    [];

  const selectedClassBasedFees = selectedFeesDetails.filter(
    (item) => item.classId !== null,
  );
  const selectedClassId =
    selectedClassBasedFees.length > 0
      ? selectedClassBasedFees[0].classId
      : null;

  const getFeeName = (item) => {
    if (item.classId === null) {
      return item.ledgerAccountDescription;
    }
    return `${item.ledgerAccountDescription} - ${item.classDescription}`;
  };

  const handleSelectAllAvailable = (isChecked) => {
    if (isChecked) {
      const availableIds = filteredAvailableFees
        .filter((item) => !formData.selectedBillItems.includes(item.id))
        .map((item) => item.id);
      setFormData({ selectedAvailableFees: availableIds });
    } else {
      setFormData({ selectedAvailableFees: [] });
    }
  };

  const handleAvailableFeeCheckboxChange = (feeId, isChecked) => {
    let updatedSelected = formData.selectedAvailableFees;
    if (isChecked) {
      updatedSelected = [...updatedSelected, feeId];
    } else {
      updatedSelected = updatedSelected.filter((id) => id !== feeId);
    }
    setFormData({ selectedAvailableFees: updatedSelected });
  };

  const handleAssignSelectedFees = () => {
    const newSelectedFees = formData.selectedBillItems.concat(
      formData.selectedAvailableFees,
    );
    setFormData({
      selectedBillItems: newSelectedFees,
      selectedAvailableFees: [],
    });
    // Track newly added IDs in edit mode
    if (isEditMode) {
      setNewlyAddedIds((prev) => {
        const newSet = new Set(prev);
        formData.selectedAvailableFees.forEach((id) => newSet.add(id));
        return newSet;
      });
    }
    setClassConflictError(null);
  };

  const handleAvailableFeeSelect = (feeId) => {
    const feeItem = billItems?.find((item) => item.id === feeId);
    if (!feeItem) return;

    if (
      feeItem.classId !== null &&
      selectedClassId !== null &&
      selectedClassId !== feeItem.classId
    ) {
      setClassConflictError(
        `Cannot select fees from different classes. Please remove the existing class-based fee first or select a different class filter.`,
      );
      return;
    }

    handleAvailableFeeCheckboxChange(feeId, true);
  };

  const handleRemoveFee = async (feeId) => {
    setRemovalError(null);
    setRemovingItemId(feeId);

    // In create mode, always remove locally
    // In edit mode, remove locally if item was NOT initially linked (newly added)
    if (!isEditMode || !initiallyLinkedIds.includes(feeId)) {
      const updatedSelected = formData.selectedBillItems.filter(
        (id) => id !== feeId,
      );
      const updatedOverrides = { ...formData.overrideAmounts };
      delete updatedOverrides[feeId];
      setFormData({
        selectedBillItems: updatedSelected,
        overrideAmounts: updatedOverrides,
      });
      // Also update newlyAddedIds
      if (isEditMode) {
        setNewlyAddedIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(feeId);
          return newSet;
        });
      }
      setRemovingItemId(null);
      return;
    }

    try {
      const result = await removeBillItem(programId, feeId);

      if (result.success) {
        const updatedSelected = formData.selectedBillItems.filter(
          (id) => id !== feeId,
        );
        const updatedOverrides = { ...formData.overrideAmounts };
        delete updatedOverrides[feeId];
        setFormData({
          selectedBillItems: updatedSelected,
          overrideAmounts: updatedOverrides,
        });
        setInitiallyLinkedIds((prev) => prev.filter((id) => id !== feeId));
      } else {
        const errorMsg = result.message || 'Failed to remove bill item';
        setRemovalError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to remove bill item';
      setRemovalError(errorMsg);
    } finally {
      setRemovingItemId(null);
    }
  };

  const handleOverrideAmountChange = (feeId, value) => {
    const feeItem = billItems?.find((item) => item.id === feeId);
    if (program.amountType === 'Percentage') {
      if (value === '' || (Number(value) >= 0 && Number(value) <= 100)) {
        setFormData({
          overrideAmounts: {
            ...formData.overrideAmounts,
            [feeId]: value,
          },
        });
      }
    } else {
      const maxAmount = feeItem?.amount || Infinity;
      if (value === '' || (Number(value) >= 0 && Number(value) <= maxAmount)) {
        setFormData({
          overrideAmounts: {
            ...formData.overrideAmounts,
            [feeId]: value,
          },
        });
      }
    }
  };

  const isValidForm = () => {
    if (formData.selectedBillItems.length === 0) {
      console.warn('Please select at least one bill item');
      return false;
    }

    for (const [feeId, amount] of Object.entries(formData.overrideAmounts)) {
      if (amount !== '' && isNaN(amount)) {
        console.warn(`Invalid amount for bill item ${feeId}: must be a number`);
        return false;
      }
      if (amount !== '' && parseFloat(amount) < 0) {
        console.warn(
          `Invalid amount for bill item ${feeId}: must be non-negative`,
        );
        return false;
      }
    }

    return true;
  };

  const handleSave = () => {
    if (isValidForm()) {
      onSave({
        selectedBillItems: formData.selectedBillItems,
        overrideAmounts: formData.overrideAmounts,
        selectedClassId: selectedClassId,
      });
    }
  };

  const getPlaceholder = () => {
    if (program.amountType === 'Percentage') {
      return `${program.amount}% — default`;
    }
    return `GHS ${program.amount} — default`;
  };

  const availableFeesForDisplay = filteredAvailableFees.filter(
    (item) => !formData.selectedBillItems.includes(item.id),
  );

  const isAllAvailableSelected =
    availableFeesForDisplay.length > 0 &&
    availableFeesForDisplay.every((item) =>
      formData.selectedAvailableFees.includes(item.id),
    );

  // Check if any selected fee is from a different class than the ones being added
  const getHasCrossClassConflict = () => {
    if (!selectedClassId || formData.selectedAvailableFees.length === 0) {
      return false;
    }
    const feesToAdd = billItems?.filter((item) =>
      formData.selectedAvailableFees.includes(item.id),
    );
    return feesToAdd?.some(
      (fee) => fee.classId !== null && fee.classId !== selectedClassId,
    );
  };

  const hasCrossClassConflict = getHasCrossClassConflict();

  const isAddButtonDisabled =
    formData.selectedAvailableFees.length === 0 || hasCrossClassConflict;

  // Check if a specific fee would cause a cross-class conflict
  const isFeeDisabled = (fee) => {
    if (fee.classId === null) return false; // General fees are always allowed
    if (!selectedClassId) return false; // No class selected yet, allow
    return fee.classId !== selectedClassId; // Disable if different class
  };

  return (
    <div
      className="d-flex-center"
      style={{ maxWidth: '1200px', margin: '0 auto' }}
    >
      <div
        style={{
          display: 'grid',
          gap: '20px',
          width: '100%',
          marginBottom: '30px',
        }}
      >
        <h2>{program.programName} — Link Bill Items</h2>
        <p>
          {isEditMode
            ? 'Manage bill items for this program. Add or remove items as needed.'
            : 'Select which bill items this program should apply to and optionally set a different amount per item.'}
        </p>
        <StageIndicator currentStage={2} />

        {classConflictError && (
          <div
            className="bx--form-requirement"
            style={{
              backgroundColor: '#fff1f1',
              border: '1px solid #da1e28',
              borderRadius: '4px',
              padding: '12px',
              color: '#da1e28',
            }}
          >
            {classConflictError}
          </div>
        )}

        <div className="students-filters d-flex" style={{ gap: '20px' }}>
          <div style={{ flex: 1 }}>
            <Select
              labelText="Filter by Class"
              value={formData.classId}
              onChange={(e) =>
                setFormData({
                  classId: e.target.value,
                  selectedAvailableFees: [],
                })
              }
            >
              <SelectItem value="" text="All Classes (General Fees Only)" />
              {allClasses?.map((cls) => (
                <SelectItem
                  key={cls.id}
                  value={cls.id}
                  text={cls.description}
                />
              ))}
            </Select>
          </div>
        </div>

        <div className="students-assignment-layout">
          <div
            className="students-column"
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: '16px',
              maxHeight: '500px',
              overflowY: 'auto',
            }}
          >
            <div
              className="d-flex-space-between"
              style={{ marginBottom: '16px' }}
            >
              <h3>Available Fees ({availableFeesForDisplay.length})</h3>
              {availableFeesForDisplay.length > 0 && (
                <div>
                  <Checkbox
                    id="select-all-available-fees"
                    labelText="Select All"
                    checked={isAllAvailableSelected}
                    onChange={handleSelectAllAvailable}
                  />
                </div>
              )}
            </div>

            {isFetchingBillItems && <p>Loading bill items...</p>}

            {!isFetchingBillItems && availableFeesForDisplay.length > 0 && (
              <div className="available-students-scroll">
                {availableFeesForDisplay.map((fee) => (
                  <div
                    key={fee.id}
                    className="d-flex-space-between"
                    style={{
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', flex: 1 }}
                    >
                      <div>
                        <Checkbox
                          id={`available-fee-${fee.id}`}
                          checked={formData.selectedAvailableFees.includes(
                            fee.id,
                          )}
                          onChange={(isChecked) =>
                            handleAvailableFeeCheckboxChange(fee.id, isChecked)
                          }
                          disabled={isFeeDisabled(fee)}
                          labelText={
                            isFeeDisabled(fee)
                              ? `Conflict: Different class (${fee.classDescription})`
                              : ''
                          }
                        />
                      </div>
                      <div style={{ marginLeft: '16px' }}>
                        <strong>{getFeeName(fee)}</strong>
                        <p
                          style={{
                            margin: '4px 0 0 0',
                            color: '#666',
                            fontSize: '14px',
                          }}
                        >
                          Amount: GHS {fee.amount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isFetchingBillItems && availableFeesForDisplay.length === 0 && (
              <p>No fees available.</p>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CustomButton
              classes="primary"
              onClick={handleAssignSelectedFees}
              disabled={isAddButtonDisabled}
              title={
                hasCrossClassConflict
                  ? 'Cannot add fees from different classes. Remove existing class-based fee first.'
                  : ''
              }
            >
              Add →<br />({formData.selectedAvailableFees.length})
            </CustomButton>
          </div>

          <div
            className="students-column"
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: '16px',
              maxHeight: '500px',
              overflowY: 'auto',
            }}
          >
            <h3>Selected Fees ({formData.selectedBillItems.length})</h3>
            <p className="text-12">
              Enter an <span className="font-bold">optional</span> override
              amount for each selected fee:
            </p>

            {removalError && (
              <p className="text-14 mt-10 text-danger">{removalError}</p>
            )}

            {formData.selectedBillItems.length > 0 && (
              <div className="assigned-students-scroll">
                {selectedFeesDetails.map((fee) => (
                  <div
                    key={fee.id}
                    className="d-flex-space-between student-assigned-row"
                    style={{
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <strong>{getFeeName(fee)}</strong>
                      <p
                        style={{
                          margin: '4px 0 0 0',
                          color: '#666',
                          fontSize: '14px',
                        }}
                      >
                        GHS {fee.amount.toFixed(2)}
                      </p>
                    </div>
                    <div
                      className="student-override-controls"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <TextInput
                        size="sm"
                        type="number"
                        value={formData.overrideAmounts[fee.id] || ''}
                        onChange={(e) =>
                          handleOverrideAmountChange(fee.id, e.target.value)
                        }
                        placeholder={getPlaceholder()}
                        style={{ width: '120px' }}
                      />
                      <CustomButton
                        classes="outline no-hover font-bold secondary"
                        onClick={() => handleRemoveFee(fee.id)}
                        style={{ fontSize: '12px', padding: '6px 12px' }}
                        isLoading={removingItemId === fee.id}
                        disabled={removingItemId === fee.id}
                      >
                        Remove
                      </CustomButton>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {formData.selectedBillItems.length === 0 && (
              <p>No fees selected yet.</p>
            )}
          </div>
        </div>

        <div className="mt-40 d-flex-space-between">
          <CustomButton
            classes="outline no-hover font-bold secondary"
            onClick={onBack}
            disabled={isLoadingProp}
          >
            Back
          </CustomButton>
          <CustomButton
            classes="font-bold primary"
            onClick={handleSave}
            disabled={formData.selectedBillItems.length === 0 || isLoadingProp}
            isLoading={isLoadingProp}
          >
            Save and Continue
          </CustomButton>
        </div>
      </div>
    </div>
  );
}
